import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Minus, X, TrendingUp, TrendingDown } from 'lucide-react';
import type { MatchScoreResult } from '../utils/matchingScoreEngine';
import {
  getMatchLevelLabel,
  getMatchLevelColor,
  getMatchLevelBg,
  BREAKDOWN_LABELS,
  BREAKDOWN_MAX,
} from '../utils/matchingScoreEngine';
import { computePresentationPriority, MEDAL_EMOJI } from '../utils/matchRanking';

interface MatchScoreDisplayProps {
  result: MatchScoreResult;
  compact?: boolean;
  rankPosition?: number;
  totalQualityMatches?: number;
}

function PriorityStars({ stars }: { stars: number }) {
  return (
    <span className="text-amber-400 tracking-tight" aria-label={`${stars} כוכבים`}>
      {'★'.repeat(stars)}
      <span className="text-gray-600">{'★'.repeat(5 - stars)}</span>
    </span>
  );
}

const MatchScoreDisplay: React.FC<MatchScoreDisplayProps> = ({
  result,
  compact = false,
  rankPosition,
  totalQualityMatches,
}) => {
  const [expanded, setExpanded] = useState(false);

  const levelLabel = getMatchLevelLabel(result.level);
  const levelColor = getMatchLevelColor(result.level);
  const levelBg = getMatchLevelBg(result.level);

  const breakdownKeys = Object.keys(BREAKDOWN_LABELS) as Array<keyof typeof BREAKDOWN_LABELS>;

  const showRank = rankPosition !== undefined && totalQualityMatches !== undefined;
  const medal = showRank ? MEDAL_EMOJI[rankPosition!] : undefined;
  const priority = computePresentationPriority(result);

  return (
    <div className="mb-4">
      {/* Score badge + rank + priority */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className={`inline-flex items-center gap-2 ${levelBg} px-3 py-1.5 rounded-lg`}>
          <span className="text-white font-bold text-lg">{result.score}%</span>
          <span className="text-white text-sm font-medium">{levelLabel}</span>
        </div>

        {showRank && (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-200 bg-gray-700/70 px-2.5 py-1 rounded-lg">
            {medal ? (
              <span className="text-base leading-none">{medal}</span>
            ) : null}
            <span>דרגה #{rankPosition} מתוך {totalQualityMatches}</span>
          </span>
        )}

        {showRank && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-300 bg-gray-700/50 px-2.5 py-1 rounded-lg" title="עדיפות הצגה">
            <PriorityStars stars={priority.stars} />
            <span className="text-gray-400">עדיפות {priority.label}</span>
          </span>
        )}

        {!compact && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            למה זו התאמה?
          </button>
        )}
      </div>

      {/* Business sentence */}
      {!compact && (
        <p className="text-sm text-gray-300 mb-2 leading-relaxed">{result.businessSentence}</p>
      )}

      {/* Strengths and Weaknesses */}
      {!compact && (result.strengths.length > 0 || result.weaknesses.length > 0) && (
        <div className="flex flex-col gap-1.5 mb-2">
          {result.strengths.length > 0 && (
            <div className="flex items-start gap-2">
              <TrendingUp size={14} className="text-green-400 mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                {result.strengths.map((s, i) => (
                  <span key={i} className="flex items-center gap-1 text-green-400">
                    <Check size={11} />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {result.weaknesses.length > 0 && (
            <div className="flex items-start gap-2">
              <TrendingDown size={14} className="text-red-400 mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                {result.weaknesses.map((w, i) => (
                  <span key={i} className="flex items-center gap-1 text-red-400">
                    <X size={11} />
                    {w}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compact field summary (collapsed state) */}
      {!compact && !expanded && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {result.partialFields.map((field, i) => (
            <span key={`p-${i}`} className="flex items-center gap-1 text-amber-400">
              <Minus size={12} />
              {field}
            </span>
          ))}
        </div>
      )}

      {/* Expandable breakdown */}
      {expanded && !compact && (
        <div className="mt-3 bg-gray-700 rounded-lg p-4 space-y-2">
          {breakdownKeys.map(key => {
            const max = BREAKDOWN_MAX[key];
            const val = result.breakdown[key];
            const pct = max > 0 ? (val / max) * 100 : 0;
            const isFull = val >= max;
            const isPartial = val > 0 && val < max;
            const isZero = val === 0;

            return (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-300">{BREAKDOWN_LABELS[key]}</span>
                  <span className={`font-medium ${isFull ? 'text-green-400' : isPartial ? 'text-amber-400' : 'text-red-400'}`}>
                    {val} / {max}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isFull ? 'bg-green-500' : isPartial ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}

          <div className="pt-2 border-t border-gray-600 flex items-center justify-between">
            <span className="text-gray-300 font-medium">סה"כ</span>
            <span className={`font-bold ${levelColor}`}>{result.score} / 100</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchScoreDisplay;
